/*
 * Copyright (c) 2014-2024 Comparatio USA  LLC. <https://www.comparatio.com>
 */

import {Component, OnDestroy, OnInit} from '@angular/core';
import {ShipmentHeadersService} from '../../../services/shipment-headers.service';
import {ActivatedRoute, Route, Router} from '@angular/router';
import {OrderHeadersService} from '../../../services/order-headers.service';
import {permissions} from '../../../permissions';
import {ToastrService} from 'ngx-toastr';
import {DatesService} from '../../../services/dates.service';
import * as moment from 'moment-timezone';
import {OrderLineService} from '../../../services/order-line.service';
import {ConfirmationService} from 'primeng/api';
import {environment} from '../../../../environments/environment';
import {Title} from '@angular/platform-browser';
import {Subscription} from 'rxjs/Subscription';

@Component({
    selector: 'app-shipment-headers-add',
    templateUrl: './shipment-headers-add.component.html',
    styleUrls: ['./shipment-headers-add.component.scss'],
    providers: [
        OrderHeadersService,
        OrderLineService,
        ShipmentHeadersService,
        DatesService
    ]
})
export class ShipmentHeadersAddComponent implements OnInit, OnDestroy {

    PO1_ID: number = null;
    po: any = [];
    rows = [];
    cols = [];
    totalRecords = 0;
    selected = [];
    public errors: any = {};
    // private units: Array<any> = [];
    private subscription: Subscription;

    public loading = false;
    public  permissions = permissions;

    private refOrderLineService = OrderLineService;
    public ban = false;

    constructor(
        private shipmentHeadersService: ShipmentHeadersService,
        private orderHeadersService: OrderHeadersService,
        private orderLineService: OrderLineService,
        private confirmationService: ConfirmationService,
        private route: ActivatedRoute,
        private router: Router,
        private toastr: ToastrService,
        private datesService: DatesService,
        private title: Title
    ) {
        this.title.setTitle('EDI - Shipments');
    }

    ngOnInit() {

        this.cols = [
            { field: 'PO2_LINE',          header: 'Line #',                 visible: true, width: '70px' },
            { field: 'ITEM_INFO',         header: 'Item Info',              visible: true, width: '30%' },
            { field: 'PO2_DESCRIPTION',   header: 'Item Desc',              visible: true, width: '30%' },
            // { field: 'UM1_ID_NEW',        header: 'UM',                     visible: true, width: '80px' },
            { field: 'PO2_UM',            header: 'UM',                     visible: true, width: '80px' },
            { field: 'QUANTITY',          header: 'Quantity Info',          visible: true, width: '120px' },
            { field: 'SH2_QUANTITY',      header: 'Qty',                    visible: true, width: '80px' },
            { field: 'SH2_SHIPMENT_DATE', header: 'Shipment Date',          visible: true, width: '110px' },
            { field: 'SH2_ARRIVAL_DATE',  header: 'Estimated Arrival Date', visible: true, width: '110px' }
        ];
        this.onWindowResize();

        this.PO1_ID = this.route.snapshot.params.id;
        this.loading = true;
        // this.orderLineService.preloadData(false).subscribe(preloadData => {
        //     this.units = preloadData.units.map(unit => {
        //         return {
        //             UM1_ID   : unit.UM1_ID,
        //             UM1_NAME : unit.UM1_NAME
        //         };
        //     });
        //     // GET DATA if using preload
        // });

        this.subscription = this.orderHeadersService.getById(this.PO1_ID).subscribe( res => {
            this.po = res.data;
            const localTime  = moment(new Date());
            const endDate    = moment(localTime);

            if (endDate.isoWeekday() === 4) { // Thu
                endDate.add(4, 'days');
            } else if (endDate.isoWeekday() === 5) { // Fri
                endDate.add(4, 'days');
            } else if (endDate.isoWeekday() === 6) { // Sat
                endDate.add(3, 'days');
            } else {
                endDate.add(2, 'days');
            }
            this.ban = false;
            this.rows = res.lines.map( item => {
                item['SH2_QUANTITY'] = parseInt( item['PO2_QUANTITY_NEW'], 10 ) - parseInt( item['PO2_TOTAL_SHIPPED'], 10 );
                item['SH2_SHIPMENT_DATE'] = localTime.toDate();
                item['SH2_ARRIVAL_DATE']  = endDate.toDate();
                if ((item['PO2_QUANTITY'] <= item['PO2_TOTAL_SHIPPED']) || (item['PO2_STATUS'] >= this.refOrderLineService['STATUS_REJECTED'])) {
                    this.ban = true;
                }
                return item;
            });
            this.totalRecords = this.rows.length;
            this.loading = false;
        }, () => {
            this.loading = false;
            window.dispatchEvent(new Event('resize'));
            this.toastr.error('TimeOut occured', 'Service Error', {
                timeOut: 3000,
            });
        });
    }

    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    onCancel() {
        this.router.navigate(['order-headers/' + this.route.snapshot.params.id]);
    }

    onSave() {
        this.confirmationService.confirm({
            header: 'Shipment Creation',
            message: 'Are you sure to create this Shipment?',
            accept: () => {
                const PO1_ID = this.route.snapshot.params.id;
                this.loading = true;
                this.shipmentHeadersService.create(this.po, this.selected).subscribe( result => {
                    this.loading = false;
                    if (!result.success) {
                        this.errors = result.errors;
                        this.toastr.error(result.errors.join('<br/>'), 'Error', {
                            timeOut: 3000,
                        });
                    } else {
                        this.toastr.success('A new shipment has been successfully created!', 'Success', {
                            timeOut: 3000,
                        });
                        this.router.navigate(['order-headers/' + PO1_ID]);
                    }
                });
            }
        });
    }

    onSelectionChange(event) {
        this.selected = this.selected.filter(selection => selection.PO2_STATUS < this.refOrderLineService.STATUS_REJECTED);
    }

    onWindowResize() {
        const smallScreenChanges = [
            {column: 'ITEM_INFO',       visible: true,  width: '35%'},
            {column: 'PO2_DESCRIPTION', visible: false, width: '30%'},
            {column: 'QUANTITY',        visible: true, width: '120px'}
        ];
        const bigScreenChanges = [
            {column: 'ITEM_INFO',       visible: true, width: '40%'},
            {column: 'PO2_DESCRIPTION', visible: true, width: '40%'},
            {column: 'QUANTITY',        visible: true, width: '25%'}
        ];
        if (window.innerWidth < environment.smallScreenThreshold) {
            this.cols.forEach(col => {
                const smallScreenChangeMatch = smallScreenChanges.find(change => change.column == col.field);
                if (smallScreenChangeMatch !== undefined) {
                    col.visible = smallScreenChangeMatch.visible;
                    col.width = smallScreenChangeMatch.width;
                }
            });
        } else {
            this.cols.forEach(col => {
                const bigScreenChangeMatch = bigScreenChanges.find(change => change.column == col.field);
                if (bigScreenChangeMatch !== undefined) {
                    col.visible = bigScreenChangeMatch.visible;
                    col.width = bigScreenChangeMatch.width;
                }
            });
        }
    }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/shipment-headers/shipment-headers-add/shipment-headers-add.component.ts